import { SUPPORTED_LANGUAGES } from '@inboundx/shared/config';
import { logger } from '@inboundx/shared/platform';

const OG_METADATA_URL = import.meta.env.VITE_OG_METADATA_URL || 'https://og.userose.ai';
const OG_METADATA_API_KEY = import.meta.env.VITE_OG_METADATA_API_KEY || '';
const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;

interface SiteBrandMetadataResponse {
    primary_color?: unknown;
    primary_color_source?: unknown;
    language?: unknown;
    additional_languages?: unknown;
}

export interface SiteMetadata {
    primaryColor: string | null;
    language: string | null;
    additionalLanguages: string[];
}

export async function fetchSiteMetadata(
    url: string,
    signal?: AbortSignal
): Promise<SiteMetadata | null> {
    try {
        logger.debug('Site primary color lookup started', { url, endpoint: OG_METADATA_URL });
        const response = await fetch(OG_METADATA_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-API-Key': OG_METADATA_API_KEY,
            },
            body: JSON.stringify({ url }),
            signal,
        });

        if (!response.ok) {
            logger.warn('Site primary color lookup failed', { status: response.status, url });
            return null;
        }

        const data = (await response.json()) as SiteBrandMetadataResponse;
        const primaryColor =
            typeof data.primary_color === 'string' && HEX_COLOR_PATTERN.test(data.primary_color)
                ? data.primary_color
                : null;

        if (primaryColor) {
            logger.debug('Site primary color lookup succeeded', {
                url,
                primaryColor,
                source:
                    typeof data.primary_color_source === 'string'
                        ? data.primary_color_source
                        : null,
            });
        } else {
            // Expected for sites without a detectable brand color; the form keeps its default.
            logger.info('Site primary color lookup returned no usable color', {
                url,
                primaryColor: data.primary_color,
                source:
                    typeof data.primary_color_source === 'string'
                        ? data.primary_color_source
                        : null,
            });
        }

        const language = SUPPORTED_LANGUAGES.find((code) => code === data.language) ?? null;
        const detectedLanguages = Array.isArray(data.additional_languages)
            ? data.additional_languages
            : [];
        const additionalLanguages = SUPPORTED_LANGUAGES.filter(
            (code) => code !== language && detectedLanguages.includes(code)
        );
        return { primaryColor, language, additionalLanguages };
    } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
            logger.debug('Site primary color lookup aborted', { url });
            return null;
        }
        logger.warn('Site primary color lookup failed', { error, url });
        return null;
    }
}

export async function fetchSitePrimaryColor(
    url: string,
    signal?: AbortSignal
): Promise<string | null> {
    return (await fetchSiteMetadata(url, signal))?.primaryColor ?? null;
}
